import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Trophy, Star, Zap, Award, TrendingUp, Users, ArrowLeft,
  Flame, Brain, Calendar, FileCheck, GraduationCap, Heart,
  Sunrise, Moon, Flag, Footprints, Target, Crown, Medal, Sparkles, Share2
} from 'lucide-react';
import SocialShare from '@/components/SocialShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon mapping
const getBadgeIcon = (iconName) => {
  const icons = {
    'footprints': Footprints,
    'brain': Brain,
    'flame': Flame,
    'zap': Zap,
    'calendar-check': Calendar,
    'file-check': FileCheck,
    'trophy': Trophy,
    'heart-handshake': Heart,
    'sunrise': Sunrise,
    'moon': Moon,
    'flag': Flag,
    'graduation-cap': GraduationCap,
  };
  return icons[iconName] || Award;
};

const StudentGamification = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [gamification, setGamification] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchData();
      recordLogin();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [gamRes, lbRes] = await Promise.all([
        axios.get(`${API}/student/gamification`, { withCredentials: true }),
        axios.get(`${API}/student/leaderboard`, { withCredentials: true })
      ]);
      setGamification(gamRes.data);
      setLeaderboard(lbRes.data);
    } catch (error) {
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };

  const recordLogin = async () => {
    try {
      const res = await axios.post(`${API}/student/record-login`, {}, { withCredentials: true });
      if (res.data.points_awarded > 0) {
        toast.success(`+${res.data.points_awarded} points! ${res.data.streak > 1 ? `${res.data.streak} day streak!` : ''}`);
      }
    } catch (error) {
      // Login recording is non-critical - log for debugging but don't disrupt user
      console.debug('Login recording failed:', error.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/student/dashboard"
            className="p-2 rounded-xl bg-[#0A1F44] text-[#94A3B8] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Trophy className="w-8 h-8 text-[#FFD700]" />
              Achievements & Progress
            </h1>
            <p className="text-[#94A3B8]">Track your learning journey and earn rewards</p>
          </div>
        </div>

        {/* Level & Points Card */}
        {gamification && (
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#0A1F44]/50 border border-[#00AEEF]/30 rounded-2xl p-8 mb-8 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FFD700]/10 to-transparent rounded-full -mr-32 -mt-32"></div>
            
            {/* Share Level Button */}
            <div className="absolute top-4 right-4">
              <SocialShare
                type="level"
                title={gamification.level.name}
                description={`${gamification.total_points.toLocaleString()} points earned!`}
                shareUrl={`/achievements/share/level/${user?.id}`}
                variant="inline"
              />
            </div>
            
            <div className="relative grid md:grid-cols-3 gap-8">
              {/* Level Display */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center text-4xl font-black"
                    style={{ 
                      background: `linear-gradient(135deg, ${gamification.level.color}40, ${gamification.level.color}20)`,
                      border: `3px solid ${gamification.level.color}`
                    }}
                  >
                    {gamification.level.level}
                  </div>
                  <div 
                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: gamification.level.color }}
                  >
                    <Star className="w-4 h-4 text-[#060F22]" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#94A3B8] uppercase tracking-wider">Current Level</p>
                  <h2 className="text-2xl font-bold" style={{ color: gamification.level.color }}>
                    {gamification.level.name}
                  </h2>
                </div>
              </div>

              {/* Points Display */}
              <div className="flex flex-col justify-center">
                <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-1">Total Points</p>
                <p className="text-5xl font-black text-white mb-2">{gamification.total_points.toLocaleString()}</p>
                <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Zap className="w-4 h-4 text-[#FF6A00]" />
                  {gamification.points_to_next_level > 0 
                    ? `${gamification.points_to_next_level} points to next level`
                    : 'Max level reached!'
                  }
                </div>
              </div>

              {/* Progress to Next Level */}
              <div className="flex flex-col justify-center">
                <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">Level Progress</p>
                <div className="w-full bg-[#060F22] rounded-full h-4 mb-2">
                  <div 
                    className="h-4 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${gamification.level_progress}%`,
                      backgroundColor: gamification.level.color
                    }}
                  ></div>
                </div>
                <p className="text-sm text-[#94A3B8]">{gamification.level_progress}% complete</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-[#00AEEF]/20 pb-2">
          {['overview', 'badges', 'leaderboard', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`gamification-tab-${tab}`}
              className={`px-4 py-2 rounded-t-xl font-medium transition-all capitalize ${
                activeTab === tab
                  ? 'bg-[#00AEEF]/20 text-[#00AEEF] border-b-2 border-[#00AEEF]'
                  : 'text-[#94A3B8] hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && gamification && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                </div>
                <span className="text-[#94A3B8]">Badges Earned</span>
              </div>
              <p className="text-3xl font-bold text-white">
                {gamification.badges_earned} / {gamification.badges_total}
              </p>
            </div>

            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#00FFE0]/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#00FFE0]" />
                </div>
                <span className="text-[#94A3B8]">Current Level</span>
              </div>
              <p className="text-3xl font-bold" style={{ color: gamification.level.color }}>
                {gamification.level.name}
              </p>
            </div>

            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#FF6A00]/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-[#FF6A00]" />
                </div>
                <span className="text-[#94A3B8]">Total Points</span>
              </div>
              <p className="text-3xl font-bold text-white">{gamification.total_points.toLocaleString()}</p>
            </div>

            {leaderboard && (
              <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-[#94A3B8]">Your Rank</span>
                </div>
                <p className="text-3xl font-bold text-white">#{leaderboard.user_rank}</p>
              </div>
            )}

            {/* Level Progress Overview */}
            <div className="md:col-span-2 lg:col-span-4 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Level Progression</h3>
              <div className="flex items-center gap-4 overflow-x-auto pb-2">
                {gamification.all_levels.map((lvl, idx) => (
                  <div 
                    key={lvl.level}
                    className={`flex flex-col items-center min-w-[100px] ${
                      gamification.level.level >= lvl.level ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    <div 
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mb-2 ${
                        gamification.level.level === lvl.level ? 'ring-4 ring-offset-2 ring-offset-[#060F22]' : ''
                      }`}
                      style={{ 
                        backgroundColor: `${lvl.color}20`,
                        borderColor: lvl.color,
                        borderWidth: '2px',
                        ringColor: lvl.color
                      }}
                    >
                      {gamification.level.level >= lvl.level ? (
                        <Star className="w-6 h-6" style={{ color: lvl.color }} />
                      ) : (
                        <span style={{ color: lvl.color }}>{lvl.level}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium" style={{ color: lvl.color }}>{lvl.name}</p>
                    <p className="text-xs text-[#94A3B8]">{lvl.min_points}+ pts</p>
                    {idx < gamification.all_levels.length - 1 && (
                      <div className="absolute h-1 w-8 bg-[#0A1F44] translate-x-16 mt-7" style={{
                        background: gamification.level.level > lvl.level ? lvl.color : '#0A1F44'
                      }}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && gamification && (
          <div className="space-y-6">
            {/* Earned Badges */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Medal className="w-5 h-5 text-[#FFD700]" />
                Earned Badges ({gamification.badges.filter(b => b.earned).length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gamification.badges.filter(b => b.earned).map((badge) => {
                  const IconComponent = getBadgeIcon(badge.icon);
                  return (
                    <div 
                      key={badge.id}
                      className="bg-gradient-to-br from-[#FFD700]/20 to-[#FF6A00]/10 border border-[#FFD700]/30 rounded-xl p-4 text-center relative group"
                    >
                      {/* Share button on hover */}
                      {badge.award_id && (
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <SocialShare
                            type="badge"
                            title={badge.name}
                            description={badge.description}
                            shareUrl={`/achievements/share/badge/${badge.award_id}`}
                            variant="compact"
                          />
                        </div>
                      )}
                      <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h4 className="font-bold text-white mb-1">{badge.name}</h4>
                      <p className="text-xs text-[#94A3B8] mb-2">{badge.description}</p>
                      <span className="inline-block px-2 py-1 bg-[#FFD700]/20 text-[#FFD700] rounded-full text-xs">
                        +{badge.points} pts
                      </span>
                    </div>
                  );
                })}
              </div>
              {gamification.badges.filter(b => b.earned).length === 0 && (
                <p className="text-[#94A3B8] text-center py-8">No badges earned yet. Keep learning!</p>
              )}
            </div>

            {/* Locked Badges */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#94A3B8]" />
                Badges to Unlock ({gamification.badges.filter(b => !b.earned).length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gamification.badges.filter(b => !b.earned).map((badge) => {
                  const IconComponent = getBadgeIcon(badge.icon);
                  return (
                    <div 
                      key={badge.id}
                      className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4 text-center opacity-60"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#0A1F44] flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-8 h-8 text-[#94A3B8]" />
                      </div>
                      <h4 className="font-bold text-[#94A3B8] mb-1">{badge.name}</h4>
                      <p className="text-xs text-[#94A3B8]/70 mb-2">{badge.description}</p>
                      <span className="inline-block px-2 py-1 bg-[#0A1F44] text-[#94A3B8] rounded-full text-xs">
                        +{badge.points} pts
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && leaderboard && (
          <div className="space-y-6">
            {/* Your Rank Card */}
            <div className="bg-gradient-to-r from-[#FF6A00]/20 to-[#FFD700]/10 border border-[#FF6A00]/30 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#FF6A00]/30 flex items-center justify-center">
                    <span className="text-2xl font-black text-[#FF6A00]">#{leaderboard.user_rank}</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{leaderboard.user_entry?.name || 'You'}</p>
                    <p className="text-[#94A3B8]">{leaderboard.user_entry?.total_points?.toLocaleString() || 0} points</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#94A3B8]">Out of {leaderboard.total_participants} students</p>
                  <p className="text-[#00FFE0] font-medium">{leaderboard.user_entry?.badges_count || 0} badges earned</p>
                </div>
              </div>
            </div>

            {/* Leaderboard Table */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/20">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#FFD700]" />
                  Top Students
                </h3>
              </div>
              <div className="divide-y divide-[#00AEEF]/10">
                {leaderboard.leaderboard.map((entry, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-4 p-4 ${
                      entry.rank <= 3 ? 'bg-gradient-to-r from-[#FFD700]/5 to-transparent' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      entry.rank === 1 ? 'bg-[#FFD700]/30 text-[#FFD700]' :
                      entry.rank === 2 ? 'bg-gray-400/30 text-gray-300' :
                      entry.rank === 3 ? 'bg-amber-600/30 text-amber-500' :
                      'bg-[#0A1F44] text-[#94A3B8]'
                    }`}>
                      {entry.rank <= 3 ? (
                        <Trophy className="w-5 h-5" />
                      ) : (
                        entry.rank
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{entry.name}</p>
                      <p className="text-xs" style={{ color: entry.level?.color || '#94A3B8' }}>
                        {entry.level?.name || 'Beginner'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white">{entry.total_points.toLocaleString()}</p>
                      <p className="text-xs text-[#94A3B8]">{entry.badges_count} badges</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && gamification && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#00AEEF]/20">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#00FFE0]" />
                Recent Activity
              </h3>
            </div>
            {gamification.recent_activity.length > 0 ? (
              <div className="divide-y divide-[#00AEEF]/10">
                {gamification.recent_activity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 rounded-full bg-[#00FFE0]/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#00FFE0]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white">{activity.description || activity.action}</p>
                      <p className="text-xs text-[#94A3B8]">
                        {new Date(activity.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className="text-[#00FFE0] font-bold">+{activity.points}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#94A3B8] text-center py-8">No activity yet. Start learning to earn points!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentGamification;
